import {Stack, TextField, Typography} from '@mui/material';
import React, {Fragment, useState} from 'react';
import {Controller, useFormContext} from 'react-hook-form';

import {RefundTaggingEnum} from 'main/analytics/gaTagsEnum';
import {AlertInfo} from 'presentation/components';
import {DocumentEnum} from 'presentation/pages/private/refund/constants/documentEnum';
import {useSelectedDoctorStore} from 'presentation/pages/private/refund/pages/Request/store/useSelectedDoctorStore';
import {translations} from 'presentation/translations-new';
import {CouncilTypeSelect} from '../../../CouncilTypeSelect';
import {StateAutoComplete} from '../../../StateAutoComplete';
import {PersonTypeEnum, SearchDoctorByEnum} from '../../constants/types';
import {DocumentsFields} from '../DocumentFields';
import {PROVIDER_NAME_MAX_LENGTH} from './constants';
import {DoctorFieldsProps} from './types';

const {
	socialReason,
	socialReasonPlaceholder,
	doctorName,
	doctorNamePlaceholder,
	councilType: councilTypeText,
	councilNumber,
	councilNumberPlaceholder,
	state: stateText,
	providerNotFound,
	legalProviderNotFound,
	providerCPF,
} = translations['pt-br'].privateFlow.refund.receiptRegistrationPage.providerData;

const {requiredField} = translations['pt-br'].commons;

export const DoctorFields = ({
	personType,
	doctorNotFound,
	searchDoctorByWatch,
	registerOtherProviderFlow,
	searchInProvider,
}: DoctorFieldsProps) => {
	const {
		control,
		clearErrors,
		formState: {errors},
	} = useFormContext();

	const [providerNameState, setProviderNameState] = useState<string>('');
	const providerNameHasError =
		providerNameState?.length > PROVIDER_NAME_MAX_LENGTH || !!errors?.providerName;
	const professionalNameHasError = !!errors?.professionalName;
	const professionalCpfHasError = !!errors?.professionalCpf?.message;
	const councilNumberHasError = !!errors?.councilNumber;
	const isLegalPerson = personType === PersonTypeEnum.LEGAL_PERSON;
	const showCouncilFields =
		(isLegalPerson && doctorNotFound) ||
		(searchDoctorByWatch === SearchDoctorByEnum.DOCUMENT && doctorNotFound);
	const resetSelectedDoctor = useSelectedDoctorStore(state => state.reset);

	function handleOnFocus() {
		resetSelectedDoctor();
	}

	const helperTextErrorProviderName = providerNameHasError ? requiredField : null;
	const helperTextErrorProfessionalName = professionalNameHasError ? requiredField : null;
	const helperTextErrorCouncilNumberHasError = councilNumberHasError ? requiredField : null;

	return (
		<Stack gap={2}>
			{registerOtherProviderFlow ? null : (
				<AlertInfo.Root severity="warning">
					<AlertInfo.Title
						data-tag-id={RefundTaggingEnum.VISUALIZOU_MEDICO_NAO_REGISTRADO}
						sx={{fontWeight: 400}}
						title={isLegalPerson ? legalProviderNotFound : providerNotFound}
					/>
				</AlertInfo.Root>
			)}

			{isLegalPerson ? (
				<Stack gap={1}>
					<Typography color="primary" fontWeight={600}>
						{socialReason}
					</Typography>
					<Controller
						name="providerName"
						render={({field}) => (
							<TextField
								{...field}
								onChange={e => {
									const newValue = e?.target?.value;
									setProviderNameState(newValue);
									field.onChange(newValue);
									clearErrors();
								}}
								value={field.value ?? ''}
								name="providerName"
								error={providerNameHasError}
								placeholder={socialReasonPlaceholder}
								label={socialReasonPlaceholder}
								disabled={registerOtherProviderFlow}
								helperText={helperTextErrorProviderName}
							/>
						)}
					/>
				</Stack>
			) : (
				<Fragment>
					<Stack gap={1}>
						{!searchInProvider &&
						searchDoctorByWatch === SearchDoctorByEnum.COUNCIL_NUMBER ? (
							<DocumentsFields
								documentName={DocumentEnum.CPF}
								label={providerCPF}
								error={professionalCpfHasError}
								helperText={errors?.professionalCpf?.message?.toString()}
								customRegister="professionalCpf"
							/>
						) : null}
						<Typography color="primary" fontWeight={600}>
							{doctorName}
						</Typography>
						<Controller
							name="professionalName"
							render={({field}) => (
								<TextField
									{...field}
									value={field.value ?? ''}
									onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
										const {value} = event.target;
										const newValue = value.replace(/\d/g, '');
										field.onChange(newValue);
										clearErrors('professionalName');
									}}
									error={professionalNameHasError}
									placeholder={doctorNamePlaceholder}
									label={doctorNamePlaceholder}
									helperText={helperTextErrorProfessionalName}
								/>
							)}
						/>
					</Stack>

					{showCouncilFields ? (
						<Fragment>
							<Stack gap={1}>
								<Typography color="primary" fontWeight={600}>
									{councilTypeText}
								</Typography>
								<CouncilTypeSelect name="councilAcronym" />
							</Stack>

							<Stack gap={1}>
								<Typography color="primary" fontWeight={600}>
									{stateText}
								</Typography>
								<StateAutoComplete name="councilState" />
							</Stack>

							<Stack gap={1}>
								<Typography color="primary" fontWeight={600}>
									{councilNumber}
								</Typography>
								<Controller
									control={control}
									name="councilNumber"
									render={({field: {value, onChange}}) => (
										<TextField
											value={value ?? ''}
											error={councilNumberHasError}
											onChange={event => {
												const filteredValue = event.target.value.replace(
													/\D/g,
													'',
												);
												onChange(filteredValue);
												clearErrors('councilNumber');
											}}
											slotProps={{htmlInput: {inputMode: 'numeric'}}}
											placeholder={councilNumberPlaceholder}
											label={councilNumberPlaceholder}
											onFocus={handleOnFocus}
											helperText={helperTextErrorCouncilNumberHasError}
										/>
									)}
								/>
							</Stack>
						</Fragment>
					) : null}
				</Fragment>
			)}
		</Stack>
	);
};
